import * as Sentry from '@sentry/react'
import {
  Environment,
  environment,
  inDevelopment,
  inE2E,
  inLocalDev,
} from './environment'
import { WrappedFirestoreError } from '@breakoutlearning/firebase-repository/types'
import type { ZodError } from 'zod'
import { inFirefox } from './browser'
import type { FirebaseRepository } from '@breakoutlearning/firebase-repository/models/FirebaseRepository'

function printError(e: Error) {
  let shouldPrint = true
  // A lot of errors are expected to appear in tests and make the output very noisy
  // should you need to inspect errors, you can pass PRINT_FIRESTORE
  if (process.env.NODE_ENV === 'test') shouldPrint = false
  if (process.env.PRINT_FIRESTORE === 'test') shouldPrint = true

  if (shouldPrint) console.error(e.name, e.message, e.stack)
}

let repository: FirebaseRepository | null = null

export function setRepository(repo: FirebaseRepository) {
  repository = repo
}

function printZodError(e: ZodError) {
  console.error(e.errors)
}

const env = environment()
// We allow sentry in dev so that we can conditionally print firestore errors
const inDev = inDevelopment() || inLocalDev()

function logAndSkip(...args: unknown[]) {
  if (repository) {
    repository.logEvent('sentry_error', { data: JSON.stringify(args) })
  }

  if (env === Environment.production) return null
  // eslint-disable-next-line no-console
  console.log(`[Sentry] <${env}>`, ...args)
  return null
}

if (!inE2E()) {
  Sentry.init({
    dsn: 'https://0304b5a09de4f022f6c2a80b97b64c34@o4506953915564032.ingest.us.sentry.io/4507176853504000',
    environment: env,
    tracePropagationTargets: [],
    // Disable all integrations in development
    integrations: inDev
      ? []
      : [
          Sentry.browserTracingIntegration({
            // disabling this in FF as we're getting weird errors in firefox
            instrumentNavigation: inFirefox ? false : true,
          }),
          Sentry.replayIntegration(),
        ],
    // Performance Monitoring
    tracesSampleRate: inDev ? 0 : 0.2, //  Capture 20% of the transactions - disable in dev
    // Session Replay
    replaysSessionSampleRate: 0.01, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    beforeSend(event, hint) {
      const exception = hint?.originalException
      const isError = exception instanceof Error
      const isString = typeof exception === 'string'

      // silence livekit's updatePages error, it's benign and livekit recovers from it
      if (isError && exception.message.match(/updatePages/))
        return logAndSkip('Silenced updatePages error')

      // silence LiveKit WebRTC teardown race conditions — these occur when a server
      // message (ICE candidate or SDP answer) arrives after the peer connection is
      // already closed (e.g. user left the meeting). They are unhandled rejections
      // inside livekit-client internals and are not actionable.
      // DOMException extends Error in modern browsers but not all; check both.
      const isErrorLike = isError || exception instanceof DOMException
      if (
        isErrorLike &&
        (exception.name === 'NegotiationError' ||
          exception.name === 'InvalidStateError') &&
        exception.message.includes("signalingState is 'closed'")
      )
        return logAndSkip(
          'Silenced LiveKit teardown NegotiationError/InvalidStateError'
        )

      // livekit rejects negotiation timeout as a plain string, not an Error object
      if (isString && exception === 'negotiation timed out')
        return logAndSkip('Silenced LiveKit negotiation timed out')

      // silence Object Not Found Matching Id - these are fake positives, we don't worry about them
      if (isError && exception.message.match(/Object Not Found Matching Id/))
        return logAndSkip('Silenced Object Not Found Matching Id error')

      if (isString && exception.match(/Object Not Found Matching Id/))
        return logAndSkip('Silenced Object Not Found Matching Id error')

      // this means someone tried to load an older version of the app that is no longer supported
      if (isString && exception.match(/Importing a module script failed/))
        return null

      // ignore failed to load resource errors for sw.js - nothing we can do about these
      if (
        isString &&
        exception.match(/sw\.js/) &&
        (exception.match(/Load failed/i) ||
          exception.match(/Failed to fetch/i) ||
          exception.match(/NetworkError/i))
      )
        return null

      // if a "Load failed" TypeError contains a Firebase Storage URL, re-classify as
      // StorageFetchError so it's tracked as a real storage issue rather than dropped as noise
      if (
        isError &&
        exception instanceof TypeError &&
        exception.message.includes('Load failed') &&
        exception.message.includes('firebasestorage.googleapis.com')
      ) {
        if (event.exception?.values) {
          for (const value of event.exception.values) {
            if (value.type === 'TypeError') value.type = 'StorageFetchError'
          }
        }
        return event
      }

      // ignore generic Safari "Load failed" TypeErrors - these are unactionable network noise
      // (user offline, navigating away, ad blockers, Safari's aggressive resource cleanup).
      // Real storage failures are caught at the source and re-thrown as StorageFetchError.
      if (
        isError &&
        exception instanceof TypeError &&
        exception.message === 'Load failed'
      )
        return logAndSkip(
          'Silenced generic Load failed TypeError (Safari network noise)'
        )

      const isWrappedFirestoreError = exception instanceof WrappedFirestoreError

      // do not send events if user is not authenticated
      if (isWrappedFirestoreError && !event.user)
        return logAndSkip('No user info in event, skipping Sentry event')

      // do not send events for handled errors
      if (isWrappedFirestoreError && exception.isHandled)
        return logAndSkip('Silenced handled Firestore error')
      if (isWrappedFirestoreError) printError(exception)
      if (isError && exception.name === 'ZodError') {
        repository?.triggerOnZodError()
        printZodError(exception as ZodError)
      }

      // do not send events in development
      if (inDev) return logAndSkip('Silenced event in development environment')

      logAndSkip('Sending event to Sentry', event)

      return event
    },
  })
}
